<template>
  <!-- min-w-0（而非 min-w-fit）：`min-w-fit` = `min-width: fit-content`。同名规则还写死在
       下方 scoped 样式的 `#app` 上 —— 那处才是真正生效的放大器，两处必须一起改，
       只改 Tailwind 类名不会生效。桌面（≥961px）下 fit-content 与 0 均不生效 → 零回归。
       详见方案 §0-F。 -->
  <div id="app" class="flex flex-col min-h-screen min-w-0">
    <router-view />
    <BillingNoticeModal />
  </div>
</template>

<script>
import { onMounted, onUnmounted, watch } from 'vue';
import { useImageAssetStore } from '@/core/store/useImageAssetStore';
import { useRouter } from 'vue-router';
import { useHead } from '@vueuse/head';
import { i18n } from '@/i18n';
import BillingNoticeModal from '@/components/billing/BillingNoticeModal.vue';
import { useResultMediaStore } from '@/core/store/useResultMediaStore';
import { REFERENCE_ANALYSIS_PROFILE_VERSION } from '@/core/analysis/referenceAnalysisService';
import { initBillingNoticeManager } from '@/core/billing/billingNoticeManager';

// 页面自管 SEO 的路由：App.vue 不再为它们推断/注入全局 head（含三语言 hreflang），
// canonical / robots / alternate / OG / Twitter / JSON-LD 全部由对应页面负责。
// 见 Review 报告 §11.4.1（SEO-R1 冻结决策）。
const pageOwnedSeoRouteNames = new Set([
  'Landing',
  'blog-list',
  'blog-detail',
  'blog-category',
  'blog-tag',
  'blog-search',
  'public-result-case'
]);

export default {
  name: "App",
  components: {
    BillingNoticeModal
  },
  setup() {
    const assetStore = useImageAssetStore();
    const resultMediaStore = useResultMediaStore();
    const router = useRouter();
    const headEntry = useHead({});
    let cleanupInterval;

    // v2.1.13：启动计费通知编排（首次发放 watcher + 路由进入提醒评估）。
    // watcher 随 App 生命周期存活，捕获任意一次成功 sync 返回的首次发放标志翻转。
    initBillingNoticeManager();

    // 清理间隔时间（例如每24小时）
    const CLEANUP_INTERVAL = 24 * 60 * 60 * 1000;

    // 执行清理
    const performCleanup = async () => {
        try {
            await assetStore.cleanOldImages();
            await resultMediaStore.deleteReferenceAnalysisCacheByVersion(REFERENCE_ANALYSIS_PROFILE_VERSION);
        } catch (error) {
            console.error('[App] data cleaning error:', error);
        }
    };

    // 更新页面meta信息
    const updateMetaInfo = (to) => {
      // 页面自管 SEO 路由：清空 App 的全局 head 贡献并立即返回，
      // 交由页面自身的 useHead 全权负责（见 §11.4.1）。
      if (to.name && pageOwnedSeoRouteNames.has(to.name)) {
        headEntry.patch({});
        return;
      }
      const baseTitle = 'PhotoScope';
      let title = baseTitle;
      const siteBaseUrl = 'https://photoscope.cc';
      const supportedLangs = ['en', 'zh', 'ja'];

      if (to.name) {
        const routeKey = to.name.toLowerCase();
        const translatedTitle = i18n.global.t(`route.${routeKey}.title`);
        
        if (translatedTitle && translatedTitle !== `route.${routeKey}.title`) {
          title = to.name === 'Landing' ? translatedTitle : `${translatedTitle} | ${baseTitle}`;
        } else {
          title = to.name === 'Landing' ? baseTitle : `${to.name} | ${baseTitle}`;
        }

        const lang = supportedLangs.includes(to.params.lang) ? to.params.lang : 'en';
        const pathWithoutLang = (to.path || '/').replace(/^\/(en|zh|ja)(?=\/|$)/, '');
        const normalizedPath = pathWithoutLang === '' ? '/home' : pathWithoutLang;
        const canonicalHref = `${siteBaseUrl}/${lang}${normalizedPath}`;
        const alternatePath = normalizedPath;

        const metaInfo = {
          title: title,
          htmlAttrs: { lang },
          meta: [
            { name: 'description', content: i18n.global.t('meta.description') },
            { name: 'author', content: i18n.global.t('meta.author') },
            { name: 'robots', content: i18n.global.t('meta.robots') },
            { property: 'og:title', content: i18n.global.t('meta.ogTitle') },
            { property: 'og:description', content: i18n.global.t('meta.ogDescription') },
            { property: 'og:site_name', content: i18n.global.t('meta.ogSiteName') },
            { property: 'og:type', content: i18n.global.t('meta.ogType') },
            { property: 'og:image', content: i18n.global.t('meta.ogImage') },
            { property: 'og:locale', content: lang },
          ],
          link: [
            {
              rel: 'canonical',
              href: canonicalHref
            },
            {
              rel: 'alternate',
              hreflang: 'en',
              href: `${siteBaseUrl}/en${alternatePath}`
            },
            {
              rel: 'alternate',
              hreflang: 'zh',
              href: `${siteBaseUrl}/zh${alternatePath}`
            },
            {
              rel: 'alternate',
              hreflang: 'ja',
              href: `${siteBaseUrl}/ja${alternatePath}`
            },
            {
              rel: 'alternate',
              hreflang: 'x-default',
              href: `${siteBaseUrl}/en${alternatePath}`
            }
          ]
        };

        headEntry.patch(metaInfo);
      }
    };

    // 监听路由变化
    watch(
      () => router.currentRoute.value,
      (to) => {
        updateMetaInfo(to);
      },
      { immediate: true }
    );

    onMounted(async () => {
        // 应用启动时执行一次清理
        await performCleanup();
        
        // 设置定期清理
        cleanupInterval = setInterval(performCleanup, CLEANUP_INTERVAL);
    });

    onUnmounted(() => {
        // 清除定时器
        if (cleanupInterval) {
            clearInterval(cleanupInterval);
        }
    });

    return {};
  }
};
</script>

<style scoped>
#app {
  /* §0-F：原为 `min-width: fit-content`（随 cceb1b59「resultview布局样式优化，卡片比例」加入）。
     该值让根元素宽度 = 内容 min-content，一旦窄屏下任一子树的 min-content 超过视口
     （案例页实测 `.task-atomic-panel__tabs` 的 4 个 nowrap tab ≈ 455px），整页即被撑到 533px，
     各卡片只是「跟着变宽」的下游受害者 —— 真正需要修的是这里。
     改为 0 后根元素恒等于视口宽，压缩交给各组件自己的 ≤1120 / ≤960 / ≤720 规则。
     桌面（内容 min-content < 视口）下 fit-content 与 0 等效 → 零回归。 */
  min-width: 0;
  background-color: var(--app-page-bg);
}
</style>
